import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type AdminGalleryRange,
  type AdminGalleryTimeline,
  type AdminGalleryUser,
} from '../api/client.ts';
import {
  bucketLabel,
  bucketSpan,
  bucketTotal,
  bucketWidthLabel,
  cellValue,
  formatValue,
  GALLERY_CHART_SERIES,
  GALLERY_RANGE_LABEL,
  labelledBuckets,
  niceScale,
  stack,
  type GalleryChartMode,
} from '../lib/galleryChart.ts';
import './AdminGalleryChart.css';

/**
 * «Что приходило» — the tiles above this answer «сколько», and one photograph
 * arriving is three different uploads at three different times.
 *
 * The report this exists for is the one docs/GALLERY-DEBUG.md opens with:
 * «загрузилось пятьсот превью, и всё». Every number on the rest of the tab is a
 * running total, and a running total cannot say that — a library a quarter
 * copied looks the same whether it moved last night or stopped in March. Bars
 * against a clock say it in one glance, and the six colours say which half
 * stopped: a phone still sending thumbnails while no preview has arrived for a
 * week is a specific bug with a specific shape here.
 *
 * Its own fetch, and deliberately not part of the stats payload. That one walks
 * the disk; this is asked again on every change of person, period or unit, and
 * three clicks around the picker must not cost three walks of two hundred
 * thousand files.
 */

/* The plot, in viewBox units. The SVG scales to the panel's width, so these are
   proportions rather than pixels — but they are chosen at the width the admin
   panel actually is, where 30 columns leave each bar about 18 units wide. */
const W = 720;
const H = 198;
const PAD_L = 46;
const PAD_R = 6;
const PAD_T = 8;
const PLOT_H = 170;
const BASE = PAD_T + PLOT_H;
const LABEL_Y = 192;
/** Bars are capped rather than filling their slot; the leftover is the air. */
const MAX_BAR = 24;
/** The surface doing the separating — between segments and between columns. */
const GAP = 2;

const RANGES: AdminGalleryRange[] = ['halfHour', 'hour', 'sixHours', 'day'];

/**
 * A rectangle whose top corners are rounded and whose foot is square.
 *
 * Only the topmost segment of a stack gets one: a rounded corner means «this is
 * where the bar ends», and rounding every segment would draw six pills that no
 * longer add up to a column.
 */
function barPath(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, width / 2, height));
  return (
    `M${x} ${y + height} L${x} ${y + r} Q${x} ${y} ${x + r} ${y} ` +
    `L${x + width - r} ${y} Q${x + width} ${y} ${x + width} ${y + r} ` +
    `L${x + width} ${y + height} Z`
  );
}

export function AdminGalleryChart({
  users,
  userId,
  onUser,
}: {
  users: AdminGalleryUser[];
  /** Whose chart, or null for everybody. Held by the tab, so the table can set it. */
  userId: string | null;
  onUser: (userId: string | null) => void;
}) {
  // The hour: wide enough to hold a whole run of thumbnails, narrow enough
  // that «оно ещё идёт?» is answered by the last few columns rather than by one.
  const [range, setRange] = useState<AdminGalleryRange>('hour');
  const [mode, setMode] = useState<GalleryChartMode>('count');
  const [data, setData] = useState<AdminGalleryTimeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** The column under the pointer or the keyboard, if any. */
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);

  /**
   * One effect over the two things that change the query, and nothing else.
   *
   * `mode` is not among them on purpose: counts and bytes come back in the same
   * payload, so switching the unit is a re-render rather than a request. Nor is
   * `hover`, which would otherwise refetch the chart on every pixel the pointer
   * moved across it.
   */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    void (async () => {
      try {
        const next = await api.adminGalleryTimeline(userId, range);
        if (alive) {
          setData(next);
          setError(null);
        }
      } catch (err) {
        if (alive) setError(err instanceof ApiError ? err.message : 'Не удалось построить график');
      }
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [userId, range]);

  const pick = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => onUser(event.target.value || null),
    [onUser],
  );

  if (error && !data) return <p className="admin-note">{error}</p>;
  if (!data) return <div className="gallery-chart" />;

  const { buckets, bucketMs, totals } = data;
  const scale = niceScale(
    Math.max(...buckets.map((bucket) => bucketTotal(bucket, mode)), 0),
    mode,
  );
  const perUnit = PLOT_H / scale.top;
  const slot = (W - PAD_L - PAD_R) / buckets.length;
  const barWidth = Math.min(MAX_BAR, slot - GAP * 2);
  const labelled = labelledBuckets(buckets, bucketMs);
  const nothing = buckets.every((bucket) => bucketTotal(bucket, mode) === 0);
  const hovered = hover === null ? undefined : buckets[hover];

  return (
    <div className="gallery-chart">
      {/* One row of filters, above the plot rather than inside it. */}
      <div className="gallery-chart-head">
        <h3 className="gallery-chart-title">Что приходило</h3>

        <select
          className="gallery-chart-who"
          value={userId ?? ''}
          onChange={pick}
          aria-label="Чей график"
        >
          <option value="">Все</option>
          {users.map((user) => (
            <option key={user.userId} value={user.userId}>
              {user.username}
            </option>
          ))}
        </select>

        <div className="gallery-chart-switch" role="group" aria-label="Период">
          {RANGES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={range === option}
              onClick={() => setRange(option)}
            >
              {GALLERY_RANGE_LABEL[option]}
            </button>
          ))}
        </div>

        <div className="gallery-chart-switch" role="group" aria-label="Единицы">
          <button type="button" aria-pressed={mode === 'count'} onClick={() => setMode('count')}>
            Штуки
          </button>
          <button type="button" aria-pressed={mode === 'bytes'} onClick={() => setMode('bytes')}>
            Объём
          </button>
        </div>

        <div className="gallery-chart-switch">
          <button type="button" aria-pressed={asTable} onClick={() => setAsTable(!asTable)}>
            Таблицей
          </button>
        </div>
      </div>

      <div className={loading ? 'gallery-chart-plot is-loading' : 'gallery-chart-plot'}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Загрузки по времени">
          {/* Gridlines first and hairline-thin: they are there to be read
              through, not looked at. */}
          {scale.ticks.map((tick) => {
            const y = BASE - tick * perUnit;
            return (
              <g key={tick}>
                {tick > 0 && (
                  <line className="gallery-chart-grid" x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} />
                )}
                <text className="gallery-chart-axis" x={PAD_L - 8} y={y + 3} textAnchor="end">
                  {mode === 'bytes' ? formatValue(tick, mode) : tick.toLocaleString('ru-RU')}
                </text>
              </g>
            );
          })}
          <line className="gallery-chart-base" x1={PAD_L} y1={BASE} x2={W - PAD_R} y2={BASE} />

          {buckets.map((bucket, index) => {
            const x = PAD_L + slot * index;
            const segments = stack(bucket, mode);
            return (
              <g key={bucket.at}>
                {hover === index && (
                  <rect className="gallery-chart-band" x={x} y={PAD_T} width={slot} height={PLOT_H} />
                )}
                {segments.map((segment, depth) => {
                  const top = BASE - (segment.offset + segment.value) * perUnit;
                  // The gap is taken off the foot of every segment but the one
                  // standing on the baseline, so the column keeps its full
                  // height and the white lines land between the fills.
                  const raw = segment.value * perUnit;
                  const height = Math.max(1, depth === 0 ? raw : raw - GAP);
                  const last = depth === segments.length - 1;
                  const seat = GALLERY_CHART_SERIES.findIndex((one) => one.key === segment.key) + 1;
                  return (
                    <path
                      key={segment.key}
                      className={`gallery-chart-s${seat}`}
                      d={barPath(
                        x + (slot - barWidth) / 2,
                        top,
                        barWidth,
                        height,
                        last ? 4 : 0,
                      )}
                    />
                  );
                })}
                {/* The hit target is the whole column, not the painted pixels:
                    a bar one photograph tall is two units of height and nobody
                    lands on it. Focusable for the same reason. */}
                <rect
                  className="gallery-chart-hit"
                  x={x}
                  y={PAD_T}
                  width={slot}
                  height={PLOT_H}
                  tabIndex={0}
                  aria-label={`${bucketSpan(bucket.at, bucketMs)}: ${formatValue(bucketTotal(bucket, mode), mode)}`}
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover((current) => (current === index ? null : current))}
                  onFocus={() => setHover(index)}
                  onBlur={() => setHover((current) => (current === index ? null : current))}
                />
                {labelled.has(index) && (
                  <text
                    className="gallery-chart-axis"
                    x={x + slot / 2}
                    y={LABEL_Y}
                    textAnchor="middle"
                  >
                    {bucketLabel(bucket.at)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {nothing && !loading && (
          <p className="gallery-chart-empty">За этот период ничего не приходило.</p>
        )}

        {hover !== null && hovered !== undefined && (
          <Tip
            bucket={hovered}
            index={hover}
            count={buckets.length}
            bucketMs={bucketMs}
            mode={mode}
          />
        )}
      </div>

      {/* Always present, and carrying the window's totals: three of the six
          hues are too light to clear 3:1 against white, which is allowed only
          while nothing is identified by colour alone. */}
      <ul className="gallery-chart-legend">
        {GALLERY_CHART_SERIES.map((series, index) => (
          <li key={series.key}>
            <span
              className="gallery-chart-swatch"
              style={{ background: `var(--series-${index + 1})` }}
            />
            {series.label} <b>{formatValue(cellValue(totals[series.key], mode), mode)}</b>
          </li>
        ))}
      </ul>

      <p className="admin-note">
        Столбец — {bucketWidthLabel(bucketMs)}. Одна фотография приходит трижды: миниатюрой, превью
        и оригиналом, и каждая засчитана в тот столбец, когда доехала. «Объём» — сколько приняли
        тогда, а не сколько лежит сейчас: очистка диска прошлое не отменяет.
      </p>

      {asTable && <ChartTable data={data} mode={mode} />}
    </div>
  );
}

/**
 * The readout for one column — every series at that x, not only the one under
 * the pointer.
 *
 * Placed at the top of the plot rather than beside the bar, so it never covers
 * the column it is describing, and pushed to whichever side keeps it on screen.
 */
function Tip({
  bucket,
  index,
  count,
  bucketMs,
  mode,
}: {
  bucket: AdminGalleryTimeline['buckets'][number];
  index: number;
  count: number;
  bucketMs: number;
  mode: GalleryChartMode;
}) {
  const slot = (W - PAD_L - PAD_R) / count;
  const left = ((PAD_L + slot * (index + 0.5)) / W) * 100;
  const near = index < count * 0.25 ? 0 : index > count * 0.75 ? -100 : -50;
  const lines = GALLERY_CHART_SERIES.map((series) => ({
    series,
    value: cellValue(bucket.series[series.key], mode),
  })).filter((line) => line.value > 0);

  return (
    <div
      className="gallery-chart-tip"
      style={{ left: `${left}%`, transform: `translateX(${near}%)` }}
    >
      <div className="gallery-chart-tip-when">{bucketSpan(bucket.at, bucketMs)}</div>
      {lines.length === 0 ? (
        <div>ничего</div>
      ) : (
        <ul>
          {lines.map((line) => (
            <li key={line.series.key}>
              <span
                className="gallery-chart-key"
                style={{
                  background: `var(--series-${GALLERY_CHART_SERIES.indexOf(line.series) + 1})`,
                }}
              />
              <span className="gallery-chart-tip-value">{formatValue(line.value, mode)}</span>
              <span className="gallery-chart-tip-name">{line.series.label}</span>
            </li>
          ))}
          {lines.length > 1 && (
            <li className="gallery-chart-tip-total">
              <span className="gallery-chart-key" />
              <span className="gallery-chart-tip-value">
                {formatValue(bucketTotal(bucket, mode), mode)}
              </span>
              <span className="gallery-chart-tip-name">всего</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * The same numbers with no colour in them.
 *
 * Not a debugging aid that got left in: it is the reason the chart is allowed
 * to use hues that do not clear 3:1 against white, and it is the view somebody
 * checking a metric against SQL actually reads. Every bucket is a row, empty
 * ones included — «в тот день ничего» is the finding half the time.
 */
function ChartTable({ data, mode }: { data: AdminGalleryTimeline; mode: GalleryChartMode }) {
  return (
    <div className="gallery-chart-table">
      <table>
        <thead>
          <tr>
            <th>Когда</th>
            {GALLERY_CHART_SERIES.map((series) => (
              <th key={series.key}>{series.label}</th>
            ))}
            <th>Всего</th>
          </tr>
        </thead>
        <tbody>
          {data.buckets.map((bucket) => {
            const total = bucketTotal(bucket, mode);
            return (
              <tr key={bucket.at} className={total === 0 ? 'is-quiet' : undefined}>
                <td>{bucketSpan(bucket.at, data.bucketMs)}</td>
                {GALLERY_CHART_SERIES.map((series) => {
                  const value = cellValue(bucket.series[series.key], mode);
                  return <td key={series.key}>{value === 0 ? '—' : formatValue(value, mode)}</td>;
                })}
                <td>{total === 0 ? '—' : formatValue(total, mode)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
